import React from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTableColumn } from '@/components/common/DataTable'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PayoutCompany } from '@/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import {
  Edit,
  Trash2,
  Plus,
  Eye,
  Mail,
  Phone,
  Merge,
  Users,
  TrendingUp,
  Banknote,
  Calculator,
  MoreHorizontal,
  FileText,
  PlusCircle
} from 'lucide-react'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { DriverMaster } from '@/types/driverMaster'
import { Vehicle } from '@/types/vehicle'
import { PlatformBadge } from '@/components/ui/PlatformBadge'
import { ColumnConfig } from '@/components/ui/ColumnVisibilityControl'
import { ReportSummaryCard } from '@/components/common/ReportPage'
import { formatSEK, formatNumber } from '@/lib/formatters'
import { Platform } from '@/types'
import { Compensation, getCompensationCategoryInfo } from '@/types/compensation'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// ==================== DRIVERS ====================

// Default driver columns configuration
export const DEFAULT_DRIVER_COLUMNS: ColumnConfig[] = [
  { key: 'primaryName', label: 'Förare', visible: true, required: true },
  { key: 'contact', label: 'Kontaktinfo', visible: true },
  { key: 'platforms', label: 'Plattformar', visible: true },
  { key: 'aliases', label: 'Antal Alias', visible: true },
  { key: 'actions', label: 'Åtgärder', visible: true, required: true },
]

// Get visible driver columns based on configuration
export function getVisibleDriverColumns(
  visibleColumnKeys: string[],
  actions: DriverActions
): DataTableColumn<DriverMaster>[] {
  const allColumns = createDriverColumns(actions)
  return allColumns.filter(col => visibleColumnKeys.includes(col.key))
}

// Driver actions interface
export interface DriverActions {
  onCreateAlias: (driver: DriverMaster) => void
  onViewDetails: (driver: DriverMaster) => void
  onInitiateMerge: (driver: DriverMaster) => void
  onSecondaryDriverSelect: (driver: DriverMaster) => void
  mergeMode: boolean
  mergePrimary: DriverMaster | null
  getRowClassName: (driver: DriverMaster) => string
  getRowClickHandler: (driver: DriverMaster) => (() => void) | undefined
  // Multi-company support (optional)
  multiCompanyEnabled?: boolean
  payoutCompanies?: PayoutCompany[]
}

// Component for clickable driver name
const DriverNameCell: React.FC<{ driver: DriverMaster }> = ({ driver }) => {
  const navigate = useNavigate()

  return (
    <div>
      <div className="font-medium text-gray-900">
        <button
          className="hover:text-blue-600 hover:underline text-left"
          onClick={() => navigate(`/driver/${driver.id}`)}
        >
          {driver.primaryName}
        </button>
      </div>
      {(driver.personalCode || driver.taxiCode) && (
        <div className="text-sm text-gray-500 space-x-2">
          {driver.personalCode && (
            <span>Personnr: {driver.personalCode}</span>
          )}
          {driver.taxiCode && (
            <span>Taxi: {driver.taxiCode}</span>
          )}
        </div>
      )}
    </div>
  )
}

// Driver contact cell
const DriverContactCell: React.FC<{ driver: DriverMaster }> = ({ driver }) => {
  if (!driver.email && !driver.phoneNumber) {
    return <span className="text-gray-400">-</span>
  }

  return (
    <div className="space-y-1">
      {driver.email && (
        <div className="flex items-center gap-1 text-sm">
          <Mail className="w-3 h-3 text-gray-400" />
          <span>{driver.email}</span>
        </div>
      )}
      {driver.phoneNumber && (
        <div className="flex items-center gap-1 text-sm">
          <Phone className="w-3 h-3 text-gray-400" />
          <span>{driver.phoneNumber}</span>
        </div>
      )}
    </div>
  )
}

// Driver platform badges cell
const DriverPlatformsCell: React.FC<{ driver: DriverMaster }> = ({ driver }) => {
  if (!driver.aliases || driver.aliases.length === 0) {
    return <span className="text-gray-400">Inga alias</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {driver.aliases.map((alias, index) => (
        <PlatformBadge key={index} platform={alias.platform} />
      ))}
    </div>
  )
}

// Driver actions cell
const DriverActionsCell: React.FC<{
  driver: DriverMaster
  actions: DriverActions
}> = ({ driver, actions }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => actions.onViewDetails(driver)}>
          <Eye className="w-4 h-4 mr-2" />
          Visa detaljer
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.onCreateAlias(driver)}>
          <Plus className="w-4 h-4 mr-2" />
          Lägg till alias
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => actions.onInitiateMerge(driver)}>
          <Merge className="w-4 h-4 mr-2" />
          Slå samman förare
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Create driver columns
export function createDriverColumns(actions: DriverActions): DataTableColumn<DriverMaster>[] {
  const companyMap = new Map((actions.payoutCompanies ?? []).map(c => [c.id, c.name]))

  const base: DataTableColumn<DriverMaster>[] = [
    {
      key: 'primaryName',
      header: 'Förare',
      cell: (value, driver) => <DriverNameCell driver={driver} />,
      sortable: true,
    },
    {
      key: 'contact',
      header: 'Kontaktinfo',
      cell: (value, driver) => <DriverContactCell driver={driver} />,
      sortable: false,
    },
    {
      key: 'platforms',
      header: 'Plattformar',
      cell: (value, driver) => <DriverPlatformsCell driver={driver} />,
      sortable: false,
    },
    {
      key: 'aliases',
      header: 'Antal Alias',
      cell: (value, driver) => (
        <Badge variant="secondary">
          {driver.aliases?.length || 0}
        </Badge>
      ),
      sortable: true,
    },
  ]

  if (actions.multiCompanyEnabled) {
    base.push({
      key: 'payoutCompany',
      header: 'Bolag',
      sortable: false,
      cell: (value, driver) => (
        <span className="text-sm">
          {driver.payoutCompanyId
            ? (companyMap.get(driver.payoutCompanyId) ?? 'Okänt')
            : <span className="text-muted-foreground">Standard</span>}
        </span>
      )
    })
  }

  base.push({
    key: 'actions',
    header: 'Åtgärder',
    cell: (value, driver) => <DriverActionsCell driver={driver} actions={actions} />,
    sortable: false,
    width: '100px',
    align: 'center',
  })

  return base
}

// ==================== VEHICLES ====================

// Default vehicle columns configuration
export const DEFAULT_VEHICLE_COLUMNS: ColumnConfig[] = [
  { key: 'carRegNumber', label: 'Registreringsnummer', visible: true, required: true },
  { key: 'assignedDriver', label: 'Tilldelad Förare', visible: true },
  { key: 'actions', label: 'Åtgärder', visible: true, required: true },
]

// Get visible vehicle columns based on configuration
export function getVisibleVehicleColumns(
  visibleColumnKeys: string[],
  actions: VehicleActions
): DataTableColumn<Vehicle>[] {
  const allColumns = createVehicleColumns(actions)
  return allColumns.filter(col => visibleColumnKeys.includes(col.key))
}

// Vehicle actions interface
export interface VehicleActions {
  onDeleteVehicle: (vehicle: Vehicle) => void
  onAssignDriver: (vehicleId: string, driverId: string | null) => void
  getAssignedDriver: (vehicleId: string) => DriverMaster | null
  drivers: DriverMaster[]
  assigningDrivers: Set<string>
}

// Vehicle Registration Number Cell
const VehicleRegNumberCell: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  return (
    <div className="font-medium text-foreground">
      {vehicle.carRegNumber}
    </div>
  )
}

// Assigned Driver Cell with Select dropdown
const AssignedDriverCell: React.FC<{
  vehicle: Vehicle
  actions: VehicleActions
}> = ({ vehicle, actions }) => {
  const assignedDriver = actions.getAssignedDriver(vehicle.id)
  const isAssigning = actions.assigningDrivers.has(vehicle.id)

  return (
    <div className="flex items-center gap-2">
      <Combobox
        options={[
          { value: 'unassigned', label: 'Ingen förare' },
          ...actions.drivers.map((driver) => ({
            value: driver.id,
            label: driver.primaryName
          }))
        ]}
        value={assignedDriver?.id || 'unassigned'}
        onValueChange={(value) => actions.onAssignDriver(vehicle.id, value === 'unassigned' ? null : value)}
        disabled={isAssigning}
        placeholder="Välj förare..."
        searchPlaceholder="Sök förare..."
        className="w-[250px]"
      />
      {isAssigning && (
        <div className="ml-2">
          <SpinnerIcon size="sm" />
        </div>
      )}
    </div>
  )
}

// Actions Cell with delete button
const VehicleActionsCell: React.FC<{
  vehicle: Vehicle
  actions: VehicleActions
}> = ({ vehicle, actions }) => {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Ta bort fordon</AlertDialogTitle>
          <AlertDialogDescription>
            Är du säker på att du vill ta bort fordonet "{vehicle.carRegNumber}"?
            Detta kommer även att ta bort tilldelningen från eventuell förare.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Avbryt</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => actions.onDeleteVehicle(vehicle)}
            className="bg-red-600 hover:bg-red-700"
          >
            Ta bort
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Create vehicle columns
export function createVehicleColumns(actions: VehicleActions): DataTableColumn<Vehicle>[] {
  return [
    {
      key: 'carRegNumber',
      header: 'Registreringsnummer',
      cell: (value, vehicle) => <VehicleRegNumberCell vehicle={vehicle} />,
      sortable: true,
    },
    {
      key: 'assignedDriver',
      header: 'Tilldelad Förare',
      cell: (value, vehicle) => <AssignedDriverCell vehicle={vehicle} actions={actions} />,
      sortable: false,
    },
    {
      key: 'actions',
      header: 'Åtgärder',
      cell: (value, vehicle) => <VehicleActionsCell vehicle={vehicle} actions={actions} />,
      sortable: false,
      width: '100px',
      align: 'center',
    },
  ]
}

// ==================== SALARY REPORT ====================

// Component for clickable driver name in salary report
const SalaryDriverNameCell: React.FC<{ driverId: string; driverName: string }> = ({ driverId, driverName }) => {
  const navigate = useNavigate()

  return (
    <button
      className="hover:text-blue-600 hover:underline text-left font-medium text-gray-900"
      onClick={() => navigate(`/driver/${driverId}`)}
    >
      {driverName}
    </button>
  )
}

// Salary report actions interface
export interface SalaryReportActions {
  onGenerateSalarySpecification: (row: SalaryReportRow) => void
  onSendEmail?: (row: SalaryReportRow) => void
  onAddCompensation: (row: SalaryReportRow) => void
}

// Salary report row data structure
export interface SalaryReportRow {
  driverId: string
  driverName: string
  personalCode?: string
  email?: string
  payoutCompany?: string  // Resolved company name (from MULTI_COMPANY feature)
  grossEarnings: number
  netEarnings: number
  tips: number
  cashReceived: number  // Cash received from customers (advance on salary)
  vatAmount: number
  shareableAmount: number
  afterFleetCommission: number
  vacationPay: number
  grossSalary: number
  // Compensation fields
  netEarningsCompensations: number
  netEarningsDeductions: number
  grossSalaryCompensations: number
  grossSalaryDeductions: number
  finalGrossSalary: number
  // Employer tax fields
  employerTaxAmount: number
  totalEmployerCost: number
  netSalary: number
  netSalaryCompensations: number
  netSalaryDeductions: number
  finalNetSalary: number
  // Compensation net values (for display/export)
  netEarningsCompensationNet: number
  grossSalaryCompensationNet: number
  netSalaryCompensationNet: number
  // Tips handling fields
  untaxedTips: number
  // The actual payout ("Att utbetala"): finalNetSalary + untaxedTips - cashReceived
  amountToPay: number
  // Original values before compensations (for tooltip comparison)
  originalNetEarnings: number
  originalGrossSalary: number
  // Compensation details for tooltip functionality
  compensations: Compensation[]
  hasCompensations: boolean
  hasDeductions: boolean
  // Tax information
  taxAmount: number
  effectiveTaxRate: number  // Actual tax rate used (as percentage 0-100)
  taxTableUsed: string | null
  taxColumnUsed: number | null
  taxCalculationMethod: 'fixed' | 'table'
  isDriverSpecificTax: boolean
}

// Default salary column visibility configuration
export const DEFAULT_SALARY_COLUMNS: ColumnConfig[] = [
  {
    key: 'driverName',
    label: 'Förare',
    visible: true,
    required: true,
    align: 'left'
  },
  {
    key: 'personalCode',
    label: 'Personnummer',
    visible: false,
    align: 'left'
  },
  {
    key: 'email',
    label: 'E-post',
    visible: false,
    align: 'left'
  },
  {
    key: 'grossEarnings',
    label: 'Bruttointäkter',
    visible: true,
    align: 'right'
  },
  {
    key: 'netEarnings',
    label: 'Nettointäkter',
    visible: false,
    align: 'right'
  },
  {
    key: 'tips',
    label: 'Dricks',
    visible: true,
    align: 'right'
  },
  {
    key: 'cashReceived',
    label: 'Kontanter mottagna',
    visible: false,
    align: 'right'
  },
  {
    key: 'vatAmount',
    label: 'Moms',
    visible: false,
    align: 'right'
  },
  {
    key: 'shareableAmount',
    label: 'Delbart belopp',
    visible: false,
    align: 'right'
  },
  {
    key: 'afterFleetCommission',
    label: 'Förarandel (exkl. semester)',
    visible: false,
    align: 'right'
  },
  {
    key: 'vacationPay',
    label: 'Semesterersättning',
    visible: false,
    align: 'right'
  },
  {
    key: 'finalGrossSalary',
    label: 'Bruttolön',
    visible: true,
    align: 'right'
  },
  {
    key: 'employerTaxAmount',
    label: 'Arbetsgivaravgift',
    visible: false,
    align: 'right'
  },
  {
    key: 'totalEmployerCost',
    label: 'Total lönekostnad',
    visible: false,
    align: 'right'
  },
  {
    key: 'taxAmount',
    label: 'Skatt',
    visible: false,
    align: 'right'
  },
  {
    key: 'effectiveTaxRate',
    label: 'Skatt (%)',
    visible: false,
    align: 'right'
  },
  {
    key: 'netSalary',
    label: 'Nettolön',
    visible: false,
    align: 'right'
  },
  {
    key: 'amountToPay',
    label: 'Att utbetala',
    visible: true,
    align: 'right'
  },
  {
    key: 'netEarningsCompensationNet',
    label: 'Komp. Nettointäkter',
    visible: false,
    align: 'right'
  },
  {
    key: 'grossSalaryCompensationNet',
    label: 'Komp. Bruttolön',
    visible: false,
    align: 'right'
  },
  {
    key: 'netSalaryCompensationNet',
    label: 'Komp. efter skatt',
    visible: false,
    align: 'right'
  },
  {
    key: 'actions',
    label: 'Åtgärd',
    visible: true,
    required: true,
    align: 'right'
  }
]

// Get default salary columns, optionally including multi-company columns
export function getDefaultSalaryColumns(multiCompanyEnabled = false): ColumnConfig[] {
  const cols = [...DEFAULT_SALARY_COLUMNS]
  if (multiCompanyEnabled) {
    // Insert payoutCompany after email (index 2)
    const emailIdx = cols.findIndex(c => c.key === 'email')
    cols.splice(emailIdx + 1, 0, {
      key: 'payoutCompany',
      label: 'Bolag',
      visible: false,
      align: 'left'
    })
  }
  return cols
}

// Standard report template type
export interface StandardReportTemplate {
  id: string
  name: string
  description: string
  visibleColumns: string[] // Column keys that should be visible
}

// Standard report templates
export const STANDARD_SALARY_REPORT_TEMPLATES: StandardReportTemplate[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Standardkolumner',
    visibleColumns: [
      'driverName',
      'grossEarnings',
      'tips',
      'finalGrossSalary',
      'amountToPay',
      'actions'
    ]
  },
  {
    id: 'compensations',
    name: 'Avdrag/Kompensationer',
    description: 'Visa kompensationskolumner och summor',
    visibleColumns: [
      'driverName',
      'netEarningsCompensationNet',
      'grossSalaryCompensationNet',
      'netSalaryCompensationNet',
      'finalGrossSalary',
      'amountToPay',
      'actions'
    ]
  },
  {
    id: 'payout',
    name: 'Utbetalningsfil',
    description: 'Visa endast utbetalningsinformation',
    visibleColumns: [
      'driverName',
      'personalCode',
      'tips',
      'amountToPay',
      'actions'
    ]
  }
]

// Column definitions for salary report
export function createSalaryReportColumns(actions: SalaryReportActions): DataTableColumn<SalaryReportRow>[] {
  return [
  {
    key: 'driverName',
    header: 'Förare',
    sortable: true,
    align: 'left',
    cell: (value: string, row: SalaryReportRow) => (
      <SalaryDriverNameCell driverId={row.driverId} driverName={value} />
    )
  },
  {
    key: 'personalCode',
    header: 'Personnummer',
    sortable: true,
    align: 'left',
    cell: (value?: string) => (
      <span>
        {value || '-'}
      </span>
    )
  },
  {
    key: 'email',
    header: 'E-post',
    sortable: true,
    align: 'left',
    cell: (value?: string) => (
      <span>
        {value || '-'}
      </span>
    )
  },
  {
    key: 'payoutCompany',
    header: 'Bolag',
    sortable: true,
    align: 'left',
    cell: (value?: string) => (
      <span>{value || '—'}</span>
    )
  },
  {
    key: 'grossEarnings',
    header: 'Bruttointäkter',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'netEarnings',
    header: 'Nettointäkter',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      // Filter compensations that apply to net earnings
      const netEarningsCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'net_earnings' && comp.type === 'compensation'
      )
      const netEarningsDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'net_earnings' && comp.type === 'deduction'
      )

      const hasNetEarningsAdjustments = netEarningsCompensations.length > 0 || netEarningsDeductions.length > 0

      // If no adjustments, show plain value without tooltip
      if (!hasNetEarningsAdjustments) {
        return <span>{formatSEK(value)}</span>
      }

      const tooltipContent = (
        <div className="w-64 space-y-3">
          <div className="text-sm font-medium text-center border-b pb-2">Nettointäkter - Detaljer</div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="text-left">Ursprungligt belopp:</span>
              <span className="font-mono text-right">{formatSEK(row.originalNetEarnings)}</span>
            </div>

            {netEarningsCompensations.map(comp => {
              const categoryInfo = getCompensationCategoryInfo(comp.category)
              return (
                <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-green-600 text-left">{categoryInfo.label}</span>
                  <span className="font-mono text-green-600 text-right">+{formatSEK(comp.amount)}</span>
                </div>
              )
            })}

            {netEarningsDeductions.map(comp => {
              const categoryInfo = getCompensationCategoryInfo(comp.category)
              return (
                <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-red-600 text-left">{categoryInfo.label}</span>
                  <span className="font-mono text-red-600 text-right">-{formatSEK(comp.amount)}</span>
                </div>
              )
            })}

            <div className="border-t pt-2 mt-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-medium">
                <span className="text-left">Slutligt belopp:</span>
                <span className="font-mono text-right">{formatSEK(value)}</span>
              </div>
            </div>
          </div>
        </div>
      )

      // Create visual indicator (colored underline) based on compensation types
      let underlineStyle = {}
      let underlineClass = ''

      const hasCompensations = netEarningsCompensations.length > 0
      const hasDeductions = netEarningsDeductions.length > 0

      if (hasCompensations && hasDeductions) {
        // Both compensations and deductions: use gradient underline (green + red)
        underlineStyle = {
          textDecoration: 'underline',
          textDecorationThickness: '2px',
          textDecorationColor: 'transparent',
          backgroundImage: 'linear-gradient(to right, #16a34a 50%, #dc2626 50%)',
          backgroundSize: '100% 2px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '0 100%'
        }
        underlineClass = 'cursor-help'
      } else if (hasCompensations) {
        // Only compensations: green underline
        underlineClass = 'cursor-help underline decoration-2 decoration-green-600'
      } else if (hasDeductions) {
        // Only deductions: red underline
        underlineClass = 'cursor-help underline decoration-2 decoration-red-600'
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={underlineClass} style={underlineStyle}>
              {formatSEK(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    key: 'vatAmount',
    header: 'Moms',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'shareableAmount',
    header: 'Delbar summa',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'afterFleetCommission',
    header: 'Förarandel (exkl. semester)',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'vacationPay',
    header: 'Semesterersättning',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'tips',
    header: 'Dricks',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'cashReceived',
    header: 'Kontanter mottagna',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'finalGrossSalary',
    header: 'Bruttolön',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      // Filter compensations that apply to gross salary
      const grossSalaryCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'gross_salary' && comp.type === 'compensation'
      )
      const grossSalaryDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'gross_salary' && comp.type === 'deduction'
      )

      const hasGrossSalaryAdjustments = grossSalaryCompensations.length > 0 || grossSalaryDeductions.length > 0

      // If no adjustments, show plain value without tooltip
      if (!hasGrossSalaryAdjustments) {
        return <span>{formatSEK(value)}</span>
      }

      const tooltipContent = (
        <div className="w-64 space-y-3">
          <div className="text-sm font-medium text-center border-b pb-2">Bruttolön - Detaljer</div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="text-left">Ursprungligt belopp:</span>
              <span className="font-mono text-right">{formatSEK(row.originalGrossSalary)}</span>
            </div>

            {grossSalaryCompensations.map(comp => {
              const categoryInfo = getCompensationCategoryInfo(comp.category)
              return (
                <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-green-600 text-left">{categoryInfo.label}</span>
                  <span className="font-mono text-green-600 text-right">+{formatSEK(comp.amount)}</span>
                </div>
              )
            })}

            {grossSalaryDeductions.map(comp => {
              const categoryInfo = getCompensationCategoryInfo(comp.category)
              return (
                <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-red-600 text-left">{categoryInfo.label}</span>
                  <span className="font-mono text-red-600 text-right">-{formatSEK(comp.amount)}</span>
                </div>
              )
            })}

            <div className="border-t pt-2 mt-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-medium">
                <span className="text-left">Slutligt belopp:</span>
                <span className="font-mono text-right">{formatSEK(value)}</span>
              </div>
            </div>
          </div>
        </div>
      )

      // Create underline style based on compensation types
      let underlineStyle = {}
      let underlineClass = ''

      const hasCompensations = grossSalaryCompensations.length > 0
      const hasDeductions = grossSalaryDeductions.length > 0

      if (hasCompensations && hasDeductions) {
        // Both types: use a gradient underline
        underlineStyle = {
          textDecoration: 'underline',
          textDecorationThickness: '2px',
          textDecorationColor: 'transparent',
          backgroundImage: 'linear-gradient(to right, #16a34a 50%, #dc2626 50%)',
          backgroundSize: '100% 2px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '0 100%'
        }
        underlineClass = 'cursor-help'
      } else if (hasCompensations) {
        underlineClass = 'cursor-help underline decoration-2 decoration-green-600'
      } else if (hasDeductions) {
        underlineClass = 'cursor-help underline decoration-2 decoration-red-600'
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={underlineClass} style={underlineStyle}>
              {formatSEK(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    key: 'employerTaxAmount',
    header: 'Arbetsgivaravgift',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span>
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'totalEmployerCost',
    header: 'Total lönekostnad',
    sortable: true,
    align: 'right',
    cell: (value: number) => (
      <span className="font-semibold">
        {formatSEK(value)}
      </span>
    )
  },
  {
    key: 'taxAmount',
    header: 'Skatt',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      const taxMethod = row.taxCalculationMethod
      const taxTable = row.taxTableUsed
      const taxColumn = row.taxColumnUsed

      let methodText = ''
      if (taxMethod === 'table' && taxTable && taxColumn) {
        methodText = `Tabell ${taxTable}, Kolumn ${taxColumn}`
      } else {
        methodText = 'Fast skattesats'
      }

      return (
        <div className="text-right">
          <div className="font-medium">{formatSEK(value)}</div>
          <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
            <span>{methodText}</span>
            {row.isDriverSpecificTax && (
              <span className="text-blue-600" title="Förare har specifika skatteinställningar">
                ⚙️
              </span>
            )}
          </div>
        </div>
      )
    }
  },
  {
    key: 'effectiveTaxRate',
    header: 'Skatt (%)',
    sortable: true,
    align: 'right',
    cell: (value: number) => {
      return (
        <div className="text-right">
          <div className="font-medium">{value.toFixed(1)}%</div>
        </div>
      )
    }
  },
  {
    key: 'netSalary',
    header: 'Nettolön',
    sortable: true,
    align: 'right',
    cell: (value: number) => formatSEK(value)
  },
  {
    key: 'amountToPay',
    header: 'Att utbetala',
    sortable: true,
    align: 'right',
    exportValue: (row: SalaryReportRow) => row.amountToPay - (row.untaxedTips || 0),
    cell: (value: number, row: SalaryReportRow) => {
      // Filter compensations that apply to net salary
      const netSalaryCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'net_salary' && comp.type === 'compensation'
      )
      const netSalaryDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'net_salary' && comp.type === 'deduction'
      )

      const hasCashReceived = row.cashReceived > 0
      const untaxedTips = row.untaxedTips || 0
      const hasUntaxedTips = untaxedTips > 0
      // Tips are displayed as a separate inline line; adjustments = other salary modifications
      const hasAdjustments =
        netSalaryCompensations.length > 0 || netSalaryDeductions.length > 0 || hasCashReceived

      // Salary-only payout excludes tips (tips shown as separate line below)
      const salaryPayout = hasUntaxedTips ? value - untaxedTips : value

      // Build the main value display (plain or with tooltip for adjustments)
      let mainDisplay: React.ReactNode

      if (!hasAdjustments) {
        mainDisplay = formatSEK(salaryPayout)
      } else {
        // Tooltip: lön efter skatt -> justeringar -> kontant förskott -> att utbetala
        const tooltipContent = (
          <div className="max-w-sm">
            <div className="text-sm font-medium text-center border-b pb-2">Att utbetala - Detaljer</div>

            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span className="text-left">Lön efter skatt:</span>
                <span className="font-mono text-right">{formatSEK(row.netSalary)}</span>
              </div>

              {netSalaryCompensations.map(comp => {
                const categoryInfo = getCompensationCategoryInfo(comp.category)
                return (
                  <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-green-600 text-left">{categoryInfo.label}</span>
                    <span className="font-mono text-green-600 text-right">+{formatSEK(comp.amount)}</span>
                  </div>
                )
              })}

              {netSalaryDeductions.map(comp => {
                const categoryInfo = getCompensationCategoryInfo(comp.category)
                return (
                  <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-red-600 text-left">{categoryInfo.label}</span>
                    <span className="font-mono text-red-600 text-right">-{formatSEK(comp.amount)}</span>
                  </div>
                )
              })}

              {hasCashReceived && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-red-600 text-left">Kontant förskott</span>
                  <span className="font-mono text-red-600 text-right">-{formatSEK(row.cashReceived)}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs border-t pt-2 font-semibold">
                <span className="text-left">Att utbetala:</span>
                <span className="font-mono text-right">{formatSEK(salaryPayout)}</span>
              </div>
            </div>
          </div>
        )

        // Create underline style based on compensation types and cash
        let underlineStyle = {}
        let underlineClass = ''

        const hasCompensations = netSalaryCompensations.length > 0
        const hasDeductions = netSalaryDeductions.length > 0 || hasCashReceived

        if (hasCompensations && hasDeductions) {
          // Both types: use a gradient underline
          underlineStyle = {
            textDecoration: 'underline',
            textDecorationThickness: '2px',
            textDecorationColor: 'transparent',
            backgroundImage: 'linear-gradient(to right, #16a34a 50%, #dc2626 50%)',
            backgroundSize: '100% 2px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '0 100%'
          }
          underlineClass = 'cursor-help'
        } else if (hasCompensations) {
          underlineClass = 'cursor-help underline decoration-2 decoration-green-600'
        } else if (hasDeductions) {
          underlineClass = 'cursor-help underline decoration-2 decoration-red-600'
        }

        mainDisplay = (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={underlineClass} style={underlineStyle}>
                {formatSEK(salaryPayout)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        )
      }

      return (
        <div className="text-right">
          {mainDisplay}
          {hasUntaxedTips && (
            <div className="text-xs text-green-600">+{formatSEK(untaxedTips)} dricks</div>
          )}
        </div>
      )
    }
  },
  {
    key: 'netEarningsCompensationNet',
    header: 'Komp. Nettointäkter',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      // value is already the net value from row.netEarningsCompensationNet
      if (value === 0) {
        return <span className="text-muted-foreground">-</span>
      }

      // Filter compensations that apply to net earnings
      const netEarningsCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'net_earnings' && comp.type === 'compensation'
      )
      const netEarningsDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'net_earnings' && comp.type === 'deduction'
      )

      const tooltipContent = (
        <div className="w-64 space-y-3">
          <div className="text-sm font-medium text-center border-b pb-2">Kompensationer - Nettointäkter</div>

          <div className="space-y-2">
            {netEarningsCompensations.length > 0 && (
              <div>
                <div className="text-xs font-medium text-green-600 mb-1">Ersättningar</div>
                {netEarningsCompensations.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">+{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            {netEarningsDeductions.length > 0 && (
              <div>
                <div className="text-xs font-medium text-red-600 mb-1">Avdrag</div>
                {netEarningsDeductions.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">-{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="border-t pt-2 mt-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-medium">
                <span className="text-left">Netto:</span>
                <span className={`font-mono text-right ${value > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatSEK(value)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help ${value > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}`}>
              {formatSEK(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    key: 'grossSalaryCompensationNet',
    header: 'Komp. Bruttolön',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      // value is already the net value from row.grossSalaryCompensationNet
      if (value === 0) {
        return <span className="text-muted-foreground">-</span>
      }

      // Filter compensations that apply to gross salary
      const grossSalaryCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'gross_salary' && comp.type === 'compensation'
      )
      const grossSalaryDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'gross_salary' && comp.type === 'deduction'
      )

      const tooltipContent = (
        <div className="w-64 space-y-3">
          <div className="text-sm font-medium text-center border-b pb-2">Kompensationer - Bruttolön</div>

          <div className="space-y-2">
            {grossSalaryCompensations.length > 0 && (
              <div>
                <div className="text-xs font-medium text-green-600 mb-1">Ersättningar</div>
                {grossSalaryCompensations.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">+{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            {grossSalaryDeductions.length > 0 && (
              <div>
                <div className="text-xs font-medium text-red-600 mb-1">Avdrag</div>
                {grossSalaryDeductions.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">-{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="border-t pt-2 mt-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-medium">
                <span className="text-left">Netto:</span>
                <span className={`font-mono text-right ${value > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatSEK(value)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help ${value > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}`}>
              {formatSEK(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    key: 'netSalaryCompensationNet',
    header: 'Komp. efter skatt',
    sortable: true,
    align: 'right',
    cell: (value: number, row: SalaryReportRow) => {
      // value is already the net value from row.netSalaryCompensationNet
      if (value === 0) {
        return <span className="text-muted-foreground">-</span>
      }

      // Filter compensations that apply to net salary
      const netSalaryCompensations = row.compensations.filter(comp =>
        comp.applyTo === 'net_salary' && comp.type === 'compensation'
      )
      const netSalaryDeductions = row.compensations.filter(comp =>
        comp.applyTo === 'net_salary' && comp.type === 'deduction'
      )

      const tooltipContent = (
        <div className="w-64 space-y-3">
          <div className="text-sm font-medium text-center border-b pb-2">Kompensationer - efter skatt</div>

          <div className="space-y-2">
            {netSalaryCompensations.length > 0 && (
              <div>
                <div className="text-xs font-medium text-green-600 mb-1">Ersättningar</div>
                {netSalaryCompensations.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">+{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            {netSalaryDeductions.length > 0 && (
              <div>
                <div className="text-xs font-medium text-red-600 mb-1">Avdrag</div>
                {netSalaryDeductions.map(comp => {
                  const categoryInfo = getCompensationCategoryInfo(comp.category)
                  return (
                    <div key={comp.id} className="grid grid-cols-2 gap-2 text-xs">
                      <span className="text-left">{categoryInfo.label}</span>
                      <span className="font-mono text-right">-{formatSEK(comp.amount)}</span>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="border-t pt-2 mt-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-medium">
                <span className="text-left">Netto:</span>
                <span className={`font-mono text-right ${value > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatSEK(value)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help ${value > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}`}>
              {formatSEK(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-white border border-gray-200 shadow-lg">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    key: 'actions',
    header: 'Åtgärd',
    sortable: false,
    align: 'right',
    width: '60px',
    cell: (value: any, row: SalaryReportRow) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => actions.onAddCompensation(row)}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Tillägg/Avdrag
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => actions.onGenerateSalarySpecification(row)}>
            <FileText className="h-4 w-4 mr-2" />
            Skapa Lönespecifikation
          </DropdownMenuItem>
          {actions.onSendEmail && (
            <DropdownMenuItem
              onClick={() => actions.onSendEmail?.(row)}
              disabled={!row.email}
            >
              <Mail className="h-4 w-4 mr-2" />
              Skicka Lönespec via Email {!row.email && '(saknar email)'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
]
}

// Helper function to filter salary columns based on visibility
export function getVisibleSalaryColumns(
  visibleColumnKeys: string[],
  actions: SalaryReportActions
): DataTableColumn<SalaryReportRow>[] {
  const allColumns = createSalaryReportColumns(actions)
  return allColumns.filter(column =>
    visibleColumnKeys.includes(String(column.key))
  )
}

// Summary data structure
export interface SalaryReportSummary {
  totalDrivers: number
  totalGrossEarnings: number
  totalNetEarnings: number
  totalTips: number
  totalCashReceived: number
  totalVatAmount: number
  totalGrossSalary: number
  totalEmployerTaxAmount: number
  totalEmployerCost: number
  totalNetSalary: number
  totalFinalNetSalary: number
  totalUntaxedTips: number
  totalAmountToPay: number
}

// Create summary cards configuration
export function createSalarySummaryCards(summary: SalaryReportSummary): ReportSummaryCard[] {
  return [
    {
      title: 'Totalt antal förare',
      value: summary.totalDrivers,
      icon: Users,
      iconColor: 'text-blue-600'
    },
    {
      title: 'Totala bruttointäkter',
      value: formatSEK(summary.totalGrossEarnings),
      icon: TrendingUp,
      iconColor: 'text-green-600'
    },
    {
      title: 'Total dricks',
      value: formatSEK(summary.totalTips),
      icon: Banknote,
      iconColor: 'text-purple-600'
    },
    {
      title: 'Total bruttolön',
      value: formatSEK(summary.totalGrossSalary),
      icon: Calculator,
      iconColor: 'text-orange-600'
    }
  ]
}